/** Gemeinsamer Wortschatz. Terminologie: docs/Glossary.md. */
import type { common as en } from '../en/common';
import { windWord } from '../grammar/de';
import type { Translated } from '../types';

export const common: Translated<typeof en> = {
  // ---- allgemeine Bedienelemente
  'ui.back': 'Zurück',
  'ui.close': 'Schließen',
  'ui.closeEsc': 'Schließen (Esc)',
  'ui.dismiss': 'Verwerfen',
  'ui.reset': 'Zurücksetzen',
  'ui.random': 'Zufällig',
  'ui.fixed': 'Fest',
  'ui.none': 'Keins',
  'ui.test': 'Probe',
  'ui.showOnMap': 'Auf der Karte zeigen',
  'ui.showOnTheMap': 'Auf der Karte zeigen',
  'ui.language': 'Sprache',

  // ---- Zeit
  'time.day': 'Tag {n}',
  'time.dayOf': 'Tag {n} / {of}',
  'time.date': 'Tag {day} · {hour} Uhr',
  'time.stamp': 'T{day} {clock}',
  'time.dayShort': 'T{n}',
  'time.hhour': 'X {hour} T{day}',
  'time.hhourTag': 'X {hour}',
  'time.now': 'jetzt',
  'time.hours': '{h} Std.',
  'time.days': '{d} Tg.',
  'time.daysHours': '{d} Tg. {h} Std.',
  'time.inHours': 'in {h} Std.',
  'time.days.long': { one: '{n} Tag', other: '{n} Tage' },
  'time.temp': '{t} °C',

  // ---- Truppengattungen
  'unit.infantry': 'Infanterie',
  'unit.motorized': 'Mot. Infanterie',
  'unit.armor': 'Panzer',
  'unit.artillery': 'Artillerie',
  'unit.recon': 'Aufklärung',
  'unit.hq': 'Hauptquartier',
  'unit.decoy': 'Attrappe',
  'unit.blurb.infantry': 'Rückgrat der Front. Billig, zäh in der Verteidigung, langsam auf dem Marsch.',
  'unit.blurb.motorized': 'Infanterie auf Lastwagen. Hält wie Fußtruppen, marschiert doppelt so schnell.',
  'unit.blurb.armor':
    'Durchbruch und Verfolgung. Braucht viel Nachschub, schwach in unübersichtlichem Gelände.',
  'unit.blurb.artillery': 'Unterstützt jedes eigene Gefecht im Umkreis von zwei Feldern.',
  'unit.blurb.recon': 'Sichtweite 4. Erkennt, was sie sieht, und entzieht sich dem Gefecht.',
  'unit.blurb.hq': 'Hier gehen die Befehle hinaus. Das Fernsprechnetz deckt den Befehlsbereich.',
  'unit.blurb.decoy': 'Zeltbahn und Sperrholz. Gilt als Division, bis der Feind nahe herankommt.',
  'unit.formation.infantry': 'Infanterieverband',
  'unit.formation.motorized': 'Mot. Verband',
  'unit.formation.armor': 'Panzerverband',
  'unit.formation.artillery': 'Artillerieverband',
  'unit.formation.recon': 'Aufklärungsverband',
  'unit.formation.hq': 'Stab',
  'unit.formation.decoy': 'Scheinverband',
  'unit.unidentifiedFormation': 'unbekannter Verband',
  'unit.unidentifiedContact': 'Unbekannter Kontakt',
  'unit.aFormation': 'ein Verband',
  'unit.anHq': 'ein Hauptquartier',
  'unit.echelon.division': 'XX',
  'unit.echelon.army': 'XXXX',
  'unit.symbol.hq': 'HQ',
  'unit.symbol.decoy': 'S',
  'unit.decoyStamp': 'ATTRAPPE',

  // ---- Zustand der Verbände
  'status.idle': 'In Ruhe',
  'status.moving': 'Auf dem Marsch',
  'status.attacking': 'Im Angriff',
  'status.defending': 'In der Abwehr',
  'status.retreating': 'Im Rückzug',
  'status.entrenched': 'Eingegraben',
  'status.awaiting': 'Wartet auf X-Zeit',
  'status.routed': 'Zersprengt',

  // ---- Haltung & Befehle
  'posture.aggressive': 'Offensiv',
  'posture.balanced': 'Ausgewogen',
  'posture.defensive': 'Defensiv',
  'posture.aggressive.hint': 'Greift schwache Nachbarn aus eigenem Entschluss an',
  'posture.balanced.hint': 'Hält den Raum',
  'posture.defensive.hint': 'Weicht aus, bevor der Verband zerschlagen wird',
  'order.move': 'Marsch',
  'order.attack': 'Angriff',
  'order.hold': 'Halten',
  'order.retreat': 'Rückzug',
  'order.posture': 'Haltung',
  'channel.telephone': 'Fernsprecher',
  'channel.courier': 'Kurier',
  'channel.radio': 'Funk',

  // ---- Kampferfahrung
  'exp.0': 'Unerfahren',
  'exp.1': 'Ausgebildet',
  'exp.2': 'Kampferprobt',
  'exp.3': 'Altgedient',
  'exp.4': 'Elite',

  // ---- Himmelsrichtungen
  'compass.E': 'O',
  'compass.NE': 'NO',
  'compass.N': 'N',
  'compass.NW': 'NW',
  'compass.W': 'W',
  'compass.SW': 'SW',
  'compass.S': 'S',
  'compass.SE': 'SO',
  'wind.from': ({ dir }) => `Wind aus ${windWord(String(dir))}`,
  'wind.fromCap': ({ dir }) => `Wind aus ${windWord(String(dir))}`,

  // ---- Gelände
  'terrain.0': 'Offene See',
  'terrain.1': 'Flachwasser',
  'terrain.2': 'See',
  'terrain.3': 'Ebene',
  'terrain.4': 'Wald',
  'terrain.5': 'Hügelland',
  'terrain.6': 'Gebirge',
  'terrain.7': 'Sumpf',
  'terrain.8': 'Stadtgebiet',
  'terrain.unknown': 'Unbekannt',

  // ---- Orte
  'place.front': 'die Front',
  'place.atFront': 'an der Front',
  'place.at': 'in {name}',
  'place.near': 'bei {name}',
  'place.in': 'in {name}',
  'place.city': 'eine Stadt',
  'place.depot': 'das Depot',
  'city.size.1': 'Kleinstadt',
  'city.size.2': 'Stadt',
  'city.size.3': 'Großstadt',
  'city.capital': 'Hauptstadt',

  // ---- Nationen (Ersatzwörter)
  'nation.enemy': 'feindlich',
  'nation.theEnemy': 'der Feind',

  // ---- Jahreszeiten
  'season.spring': 'Frühjahr',
  'season.summer': 'Sommer',
  'season.autumn': 'Herbst',
  'season.winter': 'Winter',
  'season.random': 'Zufällig',
  'season.blurb.spring':
    'Tauwetter und Schauer — früh Schlamm im Tiefland, der mit den wärmeren Wochen abtrocknet.',
  'season.blurb.summer':
    'Fester Boden und lange Tage — Panzerwetter, nachmittags von Gewittern unterbrochen.',
  'season.blurb.autumn':
    'Fronten, Nebel und die Rasputiza — die Felder werden zu Schlamm, die Straßen entscheiden.',
  'season.blurb.winter':
    'Frost und Schnee — Flüsse und Sümpfe frieren tragfähig zu, Schneestürme machen blind.',
  'season.blurb.random': 'Die Jahreszeit wird mit dem Kriegsschauplatz gezogen — erst den Himmel aufklären.',

  // ---- Wetter
  'sky.clear': 'Klar',
  'sky.fair': 'Heiter',
  'sky.overcast': 'Bedeckt',
  'sky.rain': 'Regen',
  'sky.storm': 'Unwetter',
  'sky.snow': 'Schnee',
  'sky.blizzard': 'Schneesturm',
  'sky.fog': 'Nebel',
  'ground.mud': 'Schlamm',
  'ground.snow': 'Schneedecke',
  'ground.frozen': 'Gefroren',
  'ground.wet': 'Nass',
  'ground.firm': 'fest',
  'outlook.clear': 'Trocken',
  'outlook.rain': 'Regen',
  'outlook.storm': 'Unwetter',
  'outlook.snow': 'Schnee',
  'outlook.blizzard': 'Schneesturm',
  'outlook.fog': 'Nebel',

  // ---- Schwierigkeit der KI
  'difficulty.recruit': 'Rekrut',
  'difficulty.veteran': 'Veteran',
  'difficulty.general': 'General',
  'difficulty.recruit.hint': 'Reagiert langsam',
  'difficulty.veteran.hint': 'Solide Stabsarbeit',
  'difficulty.general.hint': 'Schnell und rücksichtslos',

  // ---- Kartengrößen
  'mapSize.small': 'Klein',
  'mapSize.medium': 'Mittel',
  'mapSize.large': 'Groß',
  'mapSize.hexes': '{w} × {h} Felder',
  'mapSize.players.small': '2–3 Nationen',
  'mapSize.players.medium': '3–4 Nationen',
  'mapSize.players.large': '4–6 Nationen',

  // ---- Spieltempo
  'speed.1': 'Bedächtig — etwa eine Stunde pro Sekunde',
  'speed.2': 'Ruhig',
  'speed.3': 'Normal',
  'speed.4': 'Zügig',
  'speed.5': 'Rasant',
  'speed.label': 'Tempo {v}',

  // ---- Technologie zu Beginn
  'techPreset.none': 'Stand 1924',
  'techPreset.none.hint': 'Nur Kuriere und Feldfernsprecher — der Rest will erforscht sein',
  'techPreset.modern': 'Moderner Krieg',
  'techPreset.modern.hint': 'Alle beginnen mit Feldfunk und Motorisierung',

  // ---- Bündnisse & Fristen
  'team.name': 'Bündnis {name}',
  'dayLimit.days': ({ n }) => `${n} Tage`,

  // ---- Forschung
  'branch.signals': 'Fernmeldewesen',
  'branch.intelligence': 'Nachrichtendienst',
  'branch.warfare': 'Kriegführung',
  'branch.logistics': 'Nachschubwesen',
  'tech.field_telephone.name': 'Feldfernsprechnetz',
  'tech.field_telephone.desc': 'Befehlsbereich jedes Hauptquartiers +2 Felder.',
  'tech.motor_couriers.name': 'Kradmelder',
  'tech.motor_couriers.desc': 'Kuriere sind etwa 75 % schneller.',
  'tech.radio.name': 'Feldfunk',
  'tech.radio.desc':
    'Befehle können gefunkt werden: fast ohne Verzug, doch feindliche Entzifferer lesen mit.',
  'tech.encryption.name': 'Schlüsselmaschine',
  'tech.encryption.desc': 'Die Aussicht des Feindes, unseren Funkverkehr zu entziffern, sinkt um 60 %.',
  'tech.codebreaking.name': 'Entzifferungsdienst',
  'tech.codebreaking.desc': 'Aussicht, feindliche Funkbefehle zu entziffern.',
  'tech.codebreaking2.name': 'Entzifferungsmaschinen',
  'tech.codebreaking2.desc': 'Feindliche Funkbefehle werden weit häufiger entziffert.',
  'tech.aerial_recon.name': 'Luftaufklärung',
  'tech.aerial_recon.desc':
    'Aufklärung +2 Sichtweite; tägliche Bildflüge zeigen feindliche Stellungen nahe unserer Front.',
  'tech.deception.name': 'Täuschungsverbände',
  'tech.deception.desc':
    'Scheindivisionen, die als echte Verbände gelten, bis der Feind sie aus der Nähe aufklärt.',
  'tech.motorization.name': 'Motorisierung',
  'tech.motorization.desc': 'Ermöglicht motorisierte Infanteriedivisionen.',
  'tech.armor2.name': 'Mittlere Panzer',
  'tech.armor2.desc': 'Angriffskraft der Panzer +25 %.',
  'tech.heavy_artillery.name': 'Schwere Artillerie',
  'tech.heavy_artillery.desc': 'Reichweite der Artillerieunterstützung +1, Angriff +20 %.',
  'tech.combined_arms.name': 'Gefecht der verbundenen Waffen',
  'tech.combined_arms.desc': '+20 % Angriff, wenn Panzer und Infanterie gemeinsam angreifen.',
  'tech.defense_in_depth.name': 'Tiefgestaffelte Abwehr',
  'tech.defense_in_depth.desc': 'Doppelt so schnell eingegraben; +15 % Abwehr in ausgebauter Stellung.',
  'tech.railways.name': 'Feldeisenbahnen',
  'tech.railways.desc': 'Versorgungsreichweite entlang Schiene und Straße +40 %.',
  'tech.field_hospitals.name': 'Feldlazarette',
  'tech.field_hospitals.desc': 'Verbände erholen sich doppelt so schnell und brauchen 30 % weniger Ersatz.',

  // ---- Decknamen
  'op.prefix': 'Unternehmen {name}',
  'op.names':
    'Amboss|Egge|Turmfalke|Laterne|Sense|Zunder|Steinbruch|Blasebalg|Fehdehandschuh|Glut|Wehr|Distel|Hammerschlag|Ziegenmelker|Eisentor|Mühlgraben',
};
